import {
  ConfirmContext,
  type ConfirmFunction,
  type ConfirmOptions,
} from "core/context/ConfirmContext";
import type { ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ConfirmDialog } from "./ConfirmDialog";

type ConfirmationRequest = {
  question: string;
  description?: string;
  options?: ConfirmOptions;
  resolve: (confirmed: boolean) => void;
};

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation("Fixhub", {
    keyPrefix: "Next:Core:ConfirmProvider",
  });
  const queue = useRef<ConfirmationRequest[]>([]);
  const isConfirming = useRef(false);
  const [active, setActive] = useState<ConfirmationRequest | null>(null);
  const [closing, setClosing] = useState<ConfirmationRequest | null>(null);
  const displayed = active ?? closing;

  const confirmText = t("confirmButtonText", "Confirm");
  const cancelText = t("cancelButtonText", "Cancel");

  const confirm: ConfirmFunction = useCallback(
    (
      question: string,
      descriptionOrOptions?: string | ConfirmOptions,
      options?: ConfirmOptions
    ) =>
      new Promise<boolean>((resolve) => {
        let description: string | undefined;
        let resolvedOptions = options;
        if (typeof descriptionOrOptions === "string") {
          description = descriptionOrOptions;
        } else {
          resolvedOptions = descriptionOrOptions;
        }
        const request = {
          question,
          description,
          options: resolvedOptions,
          resolve,
        };
        queue.current.push(request);
        if (queue.current.length === 1) {
          setActive(request);
        }
      }),
    []
  );

  const settle = useCallback((confirmed: boolean) => {
    const [current, ...remaining] = queue.current;
    if (!current) return;
    queue.current = remaining;
    current.resolve(confirmed);
    setClosing(current);
    setActive(remaining[0] ?? null);
  }, []);

  async function handleConfirm() {
    if (!active || isConfirming.current) return;
    const action = active.options?.onConfirm;
    if (!action) {
      settle(true);
      return;
    }
    isConfirming.current = true;
    try {
      await action();
      settle(true);
    } catch {
      return;
    } finally {
      isConfirming.current = false;
    }
  }

  useEffect(() => {
    function cleanup() {
      queue.current.forEach((request) => {
        request.resolve(false);
      });
      queue.current = [];
    }
    return cleanup;
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ConfirmDialog
        open={active !== null}
        handleClose={() => {
          if (!isConfirming.current) {
            settle(false);
          }
        }}
        onConfirmClick={handleConfirm}
        title={displayed?.question ?? ""}
        description={displayed?.description ?? ""}
        cancelButtonText={displayed?.options?.cancelText ?? cancelText}
        confirmButtonText={displayed?.options?.confirmText ?? confirmText}
        confirmButtonColor={displayed?.options?.confirmColor}
        confirmButtonIcon={displayed?.options?.confirmIcon}
      />
    </ConfirmContext.Provider>
  );
}
